import type { FC } from 'react';
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import WithdrawalCalculator from '../../_partials/WithdrawalCalculator';
import PayPalAccountBlock from './_partials/PayPalAccountBlock';
import PayPalPaymentTerms from '../../_partials/PayPalPaymentTerms';
import WithdrawalErrorBlock from '../../_partials/WithdrawalErrorBlock';
import { makeXHRCall } from '@wix/da-shared-react/pkg/simpleXHR/actionCreators';
import { getIsLoading } from '@wix/da-shared-react/pkg/simpleXHR/selectors';
import {
  SimpleXHREndpoint,
  SimpleXHRMethod,
} from '@wix/da-shared-react/pkg/simpleXHR/constants';
import { PapiRequestStudioEarningsWithdraw } from '@wix/da-papi-types';
import { getPaypalFee } from '../../../../../selectors/earnings';
import { formatFeeString } from '../../_partials/formatFeeString';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import {
  PureBiData,
  StudioEarningsWithdrawalClickBiEvent,
  StudioWithdrawalPaymentMethod,
} from '@wix/da-bi/pkg/events';

import s from './PayPalWithdrawAmountModal.scss';

export interface Props {
  isOpen: boolean;
  requestClose: () => void;
  availableBalance: number;
  paypalEmail?: string;
  minimumWithdrawalAmount?: number;
  onContinue?: () => void;
}

const PayPalWithdrawAmountModal: FC<Props> = ({
  isOpen,
  requestClose,
  availableBalance,
  paypalEmail,
  minimumWithdrawalAmount = 0,
  onContinue,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const logEvent = useBiLogger();
  const isLoading = useSelector((state: any) =>
    getIsLoading(state, SimpleXHREndpoint.WITHDRAW_EARNINGS)
  );
  const paypalFee = useSelector(getPaypalFee);
  const [amountStr, setAmountStr] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const formattedFeeString = formatFeeString(paypalFee);

  const amountNumber = (() => {
    const parsed = parseFloat(amountStr);
    if (Number.isNaN(parsed)) return 0;
    return Math.max(0, Math.min(parsed, availableBalance));
  })();

  const isAmountTooHigh = (() => {
    const parsed = parseFloat(amountStr);
    return !Number.isNaN(parsed) && parsed > availableBalance;
  })();

  const isAmountTooLow = (() => {
    const parsed = parseFloat(amountStr);
    return !Number.isNaN(parsed) && parsed < minimumWithdrawalAmount;
  })();

  const clearError = useCallback(() => {
    if (errorMessage) {
      setErrorMessage('');
    }
  }, [errorMessage]);

  const handleAmountChange = useCallback(
    (value: string) => {
      setAmountStr(value);
      clearError();
    },
    [clearError]
  );

  const calculateFee = useCallback(
    (amount: number) => {
      if (!paypalFee) {
        // Fallback to default if fee not available
        return 0.25;
      }
      const flatInDollars = parseFloat(paypalFee.flat) / 100; // Convert cents to dollars
      const relativeAsDecimal = parseFloat(paypalFee.relative) / 10000; // Convert basis points to decimal (25 -> 0.0025)
      return flatInDollars + amount * relativeAsDecimal;
    },
    [paypalFee]
  );

  // Reset amount when modal closes and clear error when modal opens
  useEffect(() => {
    if (!isOpen) {
      setAmountStr('');
      setErrorMessage('');
    } else if (isOpen) {
      setErrorMessage('');
    }
  }, [isOpen]);

  const handleWithdraw = useCallback(() => {
    logEvent(
      PureBiData<StudioEarningsWithdrawalClickBiEvent>({
        evid: 159,
        payment_method: StudioWithdrawalPaymentMethod.PayPalPayouts,
        process_step: 'withdraw',
      })
    );
    dispatch(
      makeXHRCall({
        papiBase: 'studio',
        endpoint: SimpleXHREndpoint.WITHDRAW_EARNINGS,
        method: SimpleXHRMethod.POST,
        data: {
          method: 'paypal',
          amount: amountNumber.toString(),
        } as PapiRequestStudioEarningsWithdraw,
        onSuccess: () => {
          onContinue?.();
        },
        onError: (response: any) => {
          setErrorMessage(
            response?.errorDescription ||
              t('studio.earnings.paypal.withdraw.error.message')
          );
        },
      })
    );
  }, [dispatch, amountNumber, onContinue, t]);

  return (
    <Modal
      className={s['wrapper']}
      contentClassName={s['content']}
      heading={t('studio.earnings.withdrawToPayPal.title')}
      onRequestClose={requestClose}
      showHeaderBorder
      showFooterBorder
      shouldCloseOnOverlayClick
      isOpen={isOpen}
      withScrollableContentWrapper
      footerRight={[
        <TextButton
          onClick={requestClose}
          size="large"
          variant="main"
          key="cancel"
        >
          {t('common.cancel')}
        </TextButton>,
        <BrandedButton
          onClick={handleWithdraw}
          size="large"
          variant="brand-green"
          key="continue"
          disabled={amountNumber <= 0 || isAmountTooHigh || isAmountTooLow}
          loading={isLoading}
        >
          {t('studio.earnings.withdrawal.withdrawToPayPal')}
        </BrandedButton>,
      ]}
    >
      <WithdrawalErrorBlock message={errorMessage} />

      <div className={s['subtitle']}>
        {t('studio.earnings.withdrawToPayPal.amount.question')}
      </div>

      <div className={s['note']}>
        {t('studio.earnings.withdrawToPayPal.amount.feeNote.dynamic', {
          fee: formattedFeeString,
        })}{' '}
        <a
          href={t('studio.earnings.feesKBURL')}
          className="text-link-level-2-primary"
        >
          {t('studio.earnings.withdrawToPayPal.amount.readMoreFees')}
        </a>
      </div>

      <PayPalAccountBlock
        paypalEmail={paypalEmail}
        className={s['account-block']}
      />

      <WithdrawalCalculator
        autoFocus
        withdrawAmount={amountStr}
        onAmountChange={handleAmountChange}
        calculateFee={calculateFee}
        availableBalance={availableBalance}
        minimumAmount={minimumWithdrawalAmount}
        isValidAmount={!isAmountTooHigh && !isAmountTooLow}
      />

      <PayPalPaymentTerms className={s['terms-block']} />
    </Modal>
  );
};

PayPalWithdrawAmountModal.displayName = 'PayPalWithdrawAmountModal';

export default PayPalWithdrawAmountModal;
